import { api } from './client';
import type { ApiResponse, PlatformCategory, CredentialField, AvailablePlatform } from '../types';

export type ConnectorCapability = 'social.publish' | 'deploy.preview' | 'deploy.publish' | 'storage.read' | 'storage.write' | 'storage.delete';
export type ConnectorOwnership = 'mine' | 'account' | 'legacy_site';
export interface ConnectorAssignment {
  id: string; connection_id: string; site_id: string; site_slug: string;
  capability: ConnectorCapability; grant_id: string | null; destination: Record<string, string>;
  revision: number; status: 'active' | 'revoked' | 'access_lost' | 'requeue_required' | 'unavailable';
  permissions: { use: boolean; edit: boolean; detach: boolean };
}
export interface ConnectorGrant {
  id: string; connection_id: string; account_id: string; revision: number; capability: ConnectorCapability;
  destination_constraint: Record<string, string>; expires_at: number | null;
  status: 'pending' | 'active' | 'revoked' | 'expired'; permissions: { accept: boolean; revoke: boolean };
}
export interface ConnectorConnection {
  id: string; provider: string; label: string; categories: PlatformCategory[];
  owner: { kind: 'user' | 'account' | 'legacy_site'; id: string; name: string };
  external_identity: { id: string | null; name: string | null };
  /** Only true when the server verified this external identity; metadata alone is not proof. */
  identity_verified?: boolean;
  capabilities: ConnectorCapability[]; status: 'untested' | 'healthy' | 'expired' | 'revoked' | 'error';
  tested_at: string | null; expires_at: string | null; revision: number;
  permissions: { manage: boolean; test: boolean; share: boolean; attach: boolean };
  /** Server-authorized physical deletion eligibility; absent on older servers means unavailable. */
  can_delete_saved?: boolean;
  assignments: ConnectorAssignment[]; grants?: ConnectorGrant[];
}
export interface ConnectorListOptions { site_slug?: string; account_id?: string; category?: PlatformCategory; ownership?: ConnectorOwnership }
export interface CreateConnectorRequest {
  provider: string; label: string; owner: { kind: 'user' | 'account'; id: string };
  credentials: Record<string, string>; external_identity: { id: string | null; name: string | null }; idempotency_key: string;
}
export interface AssignConnectorRequest {
  connection_id: string; grant_id: string | null; capability: ConnectorCapability;
  destination: Record<string, string>; idempotency_key: string;
}
export interface GrantConnectorRequest {
  account_id: string; capability: ConnectorCapability; destination_constraint: Record<string, string>;
  expires_at: number | null; idempotency_key: string;
}
const connectionPath = (id: string) => `/v1/connectors/${encodeURIComponent(id)}`;
const assignmentPath = (slug: string, id?: string) => `/v1/sites/${encodeURIComponent(slug)}/connector-assignments${id ? `/${encodeURIComponent(id)}` : ''}`;
export const connectorsApi = {
  list: (options: ConnectorListOptions = {}) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(options)) if (value) query.set(key, value);
    return api.get<ApiResponse<ConnectorConnection[]>>(`/v1/connectors${query.size ? `?${query}` : ''}`);
  },
  get: (id: string) => api.get<ApiResponse<ConnectorConnection>>(connectionPath(id)),
  create: (data: CreateConnectorRequest) => api.post<ApiResponse<ConnectorConnection>>('/v1/connectors', data),
  rotate: (id: string, data: { credentials: Record<string, string>; expected_revision: number; idempotency_key: string }) => api.post<ApiResponse<ConnectorConnection>>(`${connectionPath(id)}/rotate`, data),
  test: (id: string, capability: ConnectorCapability) => api.post<ApiResponse<{ healthy: boolean; message: string; tested_at: string | null }>>(`${connectionPath(id)}/test`, { capability }),
  grant: (id: string, data: GrantConnectorRequest) => api.post<ApiResponse<ConnectorGrant>>(`${connectionPath(id)}/grants`, data),
  acceptGrant: (id: string, grantId: string) => api.post<ApiResponse<ConnectorGrant>>(`${connectionPath(id)}/grants/${encodeURIComponent(grantId)}/accept`, {}),
  revokeGrant: (id: string, grantId: string) => api.delete<ApiResponse<null>>(`${connectionPath(id)}/grants/${encodeURIComponent(grantId)}`),
  assign: (slug: string, data: AssignConnectorRequest) => api.post<ApiResponse<ConnectorAssignment>>(assignmentPath(slug), data),
  updateAssignment: (slug: string, id: string, data: { destination: Record<string, string>; expected_revision: number }) => api.patch<ApiResponse<ConnectorAssignment>>(assignmentPath(slug, id), data),
  detach: (slug: string, id: string) => api.delete<ApiResponse<null>>(assignmentPath(slug, id)),
  deleteSaved: (id: string, revision: number) => api.delete<ApiResponse<null>>(`${connectionPath(id)}/saved?expected_revision=${revision}`),
  disconnect: (id: string, revision: number) => api.delete<ApiResponse<null>>(`${connectionPath(id)}?expected_revision=${revision}`),
};

export { createRequestId as createConnectorRequestKey } from '../utils/requestId';
export function connectorDestinationFields(provider: string, capability: ConnectorCapability): string[] {
  if (capability.startsWith('deploy.')) return ['account_id', 'project_name'];
  if (capability.startsWith('storage.')) return ['bucket', 'region', 'prefix'];
  const fields = provider === 'reddit' ? ['subreddit'] : provider === 'hashnode' ? ['publication_id'] : provider === 'telegram' ? ['chat_id'] : provider === 'newsletter' ? ['from_email', 'from_name', 'reply_to'] : [];
  return capability === 'social.publish' ? [...fields, 'locale'] : fields;
}

const storageFields: CredentialField[] = [
  { name: 'bucket', label: 'Bucket', field_type: 'text', required: true },
  { name: 'region', label: 'Region', field_type: 'text', required: false },
  { name: 'access_key_id', label: 'Access key ID', field_type: 'password', required: true },
  { name: 'secret_access_key', label: 'Secret access key', field_type: 'password', required: true },
  { name: 'endpoint', label: 'S3 endpoint (optional)', field_type: 'text', required: false },
];
const supabaseFields: CredentialField[] = [
  { name: 'supabase_url', label: 'Supabase URL', field_type: 'text', required: true },
  { name: 'anon_key', label: 'Supabase API key', field_type: 'password', required: true },
  { name: 'bucket', label: 'Bucket', field_type: 'text', required: true },
];
export function connectorCredentialFields(provider: string, available: AvailablePlatform[] = []): CredentialField[] {
  return (provider === 's3' ? storageFields : provider === 'supabase' ? supabaseFields : available.find((item) => item.id === provider)?.credential_fields ?? []).filter((field) => !(provider === 'cloudflare_pages' && field.name === 'project_name') && !(['telegram', 'reddit', 'hashnode'].includes(provider) && connectorDestinationFields(provider, 'social.publish').includes(field.name)));
}

export interface ConnectorBrowseFilters {
  category?: PlatformCategory;
  ownership?: ConnectorOwnership | 'all';
  view?: 'assigned' | 'available';
}
/** Accept only supported browse filters from URLs/deep links; never carry credential form values. */
export function connectorBrowseFilters(input: Record<string, unknown>): ConnectorBrowseFilters {
  return {
    category: input.category === 'social' || input.category === 'deployment' || input.category === 'storage' ? input.category : undefined,
    ownership: input.ownership === 'all' || input.ownership === 'mine' || input.ownership === 'account' || input.ownership === 'legacy_site' ? input.ownership : undefined,
    view: input.view === 'assigned' || input.view === 'available' ? input.view : undefined,
  };
}
